import React, { useState, useEffect } from 'react';
import * as Yup from 'yup';
import Modal from 'shared/modals/Modal';
import { Field, FormElement } from '@progress/kendo-react-form';
import { saveOrganizationPreferredPool } from '../../../fetch/organizations';
import { getChassisPoolMembers } from 'src/fetch/chassisPool';
import { useToast } from 'shared/toast';
import TerminalDropdown from '../../../shared/components/dropdowns/TerminalDropdown';
import { CancelButton, DeleteButton, SaveButton } from '../../../shared/components/buttons';
import ChassisPoolsDropdown from '../../../shared/components/dropdowns/ChassisPoolsDropdown';
import { FormNumericTextBox, FormToggle } from '../../../shared/components/formControls';
import BaseForm from '../../../shared/components/forms/BaseForm';
import ChassisPoolsMembersDropdown from '../../../shared/components/dropdowns/ChassisPoolsMembersDropdown';

const ChassisPoolsModal = ({
  organizationId, initialValues, onClose, isOpen,
}) => {
  const chassisPoolSchema = Yup.object().shape({
    terminalId: Yup.mixed().when('chassisProviderId', {
      is: (chassisProviderId) => !!chassisProviderId,
      then: Yup.mixed().required('Market is Required').typeError('Market is Required'),
    }),
    chassisPoolId: Yup.mixed().required('Chassis Pool is Required'),
    chassisProviderId: Yup.mixed().nullable(),
    isProvided: Yup.bool().nullable(),
    rackRate: Yup.number().nullable(),
    designatedRate: Yup.number().nullable(),
    gwiDiscount: Yup.number().nullable(),
  });

  const [chassisProviders, setChassisProviders] = useState([]);
  const [isProvided, setIsProvided] = useState(initialValues?.isProvided);
  const [standardRate, setStandardRate] = useState(initialValues?.standardRate);
  const [designatedRate, setDesignatedRate] = useState(initialValues?.designatedRate);
  const [gwiDiscount, setGwiDiscount] = useState(initialValues?.gwiDiscount);
  const [isPercent, setIsPercent] = useState(initialValues?.discountIsPercent);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {

    setIsProvided(initialValues?.isProvided);
    setStandardRate(initialValues?.standardRate);
    setDesignatedRate(initialValues?.designatedRate);
    setGwiDiscount(initialValues?.gwiDiscount);
    setIsPercent(initialValues?.discountIsPercent);

  }, [initialValues]);

  const fetchChassisPoolMembers = async (chassisPoolId) => {
    const response = await getChassisPoolMembers(chassisPoolId);
    setChassisProviders(response?.data);
    return response?.data;
  };

  const toast = useToast();
  const formRef = React.useRef();

  const handleCancel = () => {
    onClose();
  };

  const handleDelete = () => {
    const value = { ...convertValuesToSubmit(initialValues), isDeleted: true };
    saveOrganizationPreferredPool(organizationId, value)
      .then(() => {
        toast.success({ message: 'Deleted successfully' });
        onClose();
      })
      .catch((error) => {
        toast.error({ message: 'Failed to delete', error });
      });
  };

  const convertValuesToSubmit = ({
    terminalId, chassisPoolId, chassisProviderId, ...others
  }) => ({
    ...others,
    terminalId: typeof terminalId === 'object' ? terminalId?.value : terminalId,
    chassisPoolId: typeof chassisPoolId === 'object' ? chassisPoolId?.value : chassisPoolId,
    chassisPoolMemberId:
      typeof chassisProviderId === 'object'
        ? chassisProviderId?.id
        : chassisProviders?.find((p) => p.chassisProviderId === chassisProviderId)?.id,
  });

  const handleSubmit = (value) => saveOrganizationPreferredPool(organizationId, convertValuesToSubmit(value))
    .then(() => {
      toast.success({ message: 'Saved Successfully' });
      onClose();
    })
    .catch((error) => {
      toast.error({ message: 'Failed to save', error });
    });

  useEffect(() => {
    setIsProvided(initialValues.isProvided);
  }, [initialValues]);

  useEffect(() => {
    if (initialValues.chassisPoolId) {
      fetchChassisPoolMembers(initialValues.chassisPoolId);
    }
    setLoaded(true);
  }, [initialValues.chassisPoolId]);

  const handleChassisPoolChange = (value) => {
    if (!value || !value.value) {
      formRef.current?.valueSetter('chassisProviderId', null);
      setChassisProviders([]);
      return;
    }

    formRef.current?.valueSetter('chassisProviderId', null);
    fetchChassisPoolMembers(value.value);
  };

  const handleChassisPoolMemberChange = (value) => {
    const selectedMember = chassisProviders?.find((p) => p.chassisProviderId === value);
    setDesignatedRate(selectedMember?.designatedRate);
    setStandardRate(selectedMember?.standardRate);
    setGwiDiscount(selectedMember?.gwiDiscount);
    setIsPercent(selectedMember?.discountIsPercent);
  };

  const formIsValid = (errors) => !errors.terminalId && !errors.chassisPoolId;

  const getTerminal = (valueGetter) => valueGetter('terminalId')?.value || valueGetter('terminalId');

  return (
    <Modal isOpen={isOpen} handleClose={handleCancel} containerStyle='large'>
      <div className='modal-header'>
        <h5 className='modal-title' id='addAdditionalChargeModal'>
          Set Chassis Pool
        </h5>
        <button type='button' className='button large close' onClick={handleCancel} />
      </div>
      <section className="additional-charges-form modal-body" style={{ overflowX: 'visible' }}>
        {loaded && (
          <BaseForm
            ref={formRef}
            onSubmit={handleSubmit}
            initialValues={initialValues}
            key={JSON.stringify(initialValues)}
            id='chassis-pool'
            validationSchema={chassisPoolSchema}
            render={({ valueGetter, onChange, errors }) => (
              <FormElement>
                <fieldset className="k-form-fieldset" style={{ marginBottom: `${isProvided ? '0.6rem' : '0.5rem'}` }}>
                  <Field
                    id='terminalId'
                    name='terminalId'
                    label='Market'
                    component={TerminalDropdown}
                    onChange={() => {
                      onChange('chassisProviderId', { value: null });
                    }}
                  />
                  <Field
                    id='chassisPoolId'
                    name='chassisPoolId'
                    label='Chassis Pool'
                    component={ChassisPoolsDropdown}
                    onChange={(e) => handleChassisPoolChange(e.target.value)}
                  />
                  <Field
                    id='chassisProviderId'
                    name='chassisProviderId'
                    label='Chassis Pool Member/Provider'
                    component={(props) => (
                      <ChassisPoolsMembersDropdown
                        {...props}
                        data={chassisProviders && chassisProviders
                          ?.filter((c) => c?.marketId === getTerminal(valueGetter))
                          .map((t) => ({ label: t?.chassisProviderName, value: t?.chassisProviderId, id: t?.id }))}
                      />
                    )}
                    onChange={(selected) => {
                      handleChassisPoolMemberChange(selected?.value?.value);
                    }}
                  />
                  <Field
                    id='isProvided'
                    name='isProvided'
                    label='Provided?'
                    component={FormToggle}
                    onChange={(e) => setIsProvided(e.value)}
                  />
                  {!isProvided && (
                    <>
                      <FormNumericTextBox
                        id='standardRate'
                        name='standardRate'
                        label='Rack Rate'
                        format='c2'
                        value={standardRate}
                        disabled
                      />
                      <FormNumericTextBox
                        id='designatedRate'
                        name='designatedRate'
                        label='Designated Rate'
                        format='c2'
                        value={designatedRate}
                        disabled
                      />
                      <FormNumericTextBox
                        id='gwiDiscount'
                        name='gwiDiscount'
                        label='Gwi Discount'
                        format='p'
                        value={gwiDiscount}
                        disabled
                      />
                      <FormToggle
                        id='discountIsPercent'
                        name='discountIsPercent'
                        label='Is Percent'
                        value={isPercent}
                        disabled
                      />
                    </>
                  )}
                </fieldset>
                <section className='toolbar toolbar--xxs'>
                  <div className='toolbar--column toolbar--left'>
                    <SaveButton type='submit' disabled={!formIsValid(errors)} />
                    <CancelButton onClick={handleCancel} />
                  </div>
                  <div className='toolbar--column toolbar--right'>
                    {initialValues && initialValues.id && <DeleteButton onClick={handleDelete} />}
                  </div>
                </section>
              </FormElement>
            )}
          />
        )}
      </section>
    </Modal>
  );
};

export default ChassisPoolsModal;
